import i18n, { t } from '../i18n';

const EquipmentType = {
  'AK-47': 'Rifles',
  AUG: 'Rifles',
  AWP: 'Rifles',
  'PP-Bizon': 'SMGs',
  C4: 'Misc',
  'Desert Eagle': 'Pistols',
  'Decoy Grenade': 'Grenades',
  'Dual Berettas': 'Pistols',
  FAMAS: 'Rifles',
  'Five-SeveN': 'Pistols',
  Flashbang: 'Grenades',
  G3SG1: 'Rifles',
  'Galil AR': 'Rifles',
  'Glock-18': 'Pistols',
  'HE Grenade': 'Grenades',
  P2000: 'Pistols',
  'Incendiary Grenade': 'Grenades',
  M249: 'LMGs',
  M4A4: 'Rifles',
  'MAC-10': 'SMGs',
  'MAG-7': 'Shotguns',
  Molotov: 'Grenades',
  MP7: 'SMGs',
  'MP5-SD': 'SMGs',
  MP9: 'SMGs',
  Negev: 'LMGs',
  Nova: 'Shotguns',
  P250: 'Pistols',
  P90: 'SMGs',
  'Sawed-Off': 'Shotguns',
  'SCAR-20': 'Rifles',
  'SG 553': 'Rifles',
  'Smoke Grenade': 'Grenades',
  'SSG 08': 'Rifles',
  'Tec-9': 'Pistols',
  'UMP-45': 'SMGs',
  XM1014: 'Shotguns',
  M4A1: 'Rifles',
  'CZ75 Auto': 'Pistols',
  'USP-S': 'Pistols',
  World: 'Misc',
  'R8 Revolver': 'Pistols',
  'Kevlar Vest': 'Misc',
  'Kevlar + Helmet': 'Misc',
  'Defuse Kit': 'Misc',
  Knife: 'Misc',
  UNKNOWN: 'Misc',
};

function getEquipmentTypeByName(name) {
  return EquipmentType[name] || null; // Return null if not found
}

function categorizeWeapons(weaponStats) {
  if (!weaponStats) return {};

  const weaponTypes = {
    Pistols: [],
    SMGs: [],
    Rifles: [],
    LMGs: [],
    Shotguns: [],
    Misc: [],
  };

  for (const weaponStat of weaponStats) {
    const weaponType = getEquipmentTypeByName(weaponStat.weapon_name);
    if (weaponTypes[weaponType]) {
      weaponTypes[weaponType].push(weaponStat);
    } else {
      weaponTypes.Misc.push(weaponStat);
    }
  }

  return weaponTypes;
}

function getAvailableWeapons(weaponStats, weaponTypes, selectedWeaponType) {
  if (!weaponStats) return [];

  const weapons = [];

  if (selectedWeaponType === '') {
    Object.keys(weaponTypes).forEach((type) => {
      if (type !== 'Misc') {
        weaponTypes[type].forEach((weapon) => {
          weapons.push({ name: weapon.weapon_name, has_data: weapon.has_data !== false });
        });
      }
    });
  } else {
    weaponTypes[selectedWeaponType]?.forEach((weapon) => {
      weapons.push({ name: weapon.weapon_name, has_data: weapon.has_data !== false });
    });
  }

  return weapons.sort((a, b) => {
    // Weapons with data first, then alphabetical
    if (a.has_data !== b.has_data) return a.has_data ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

function getFilteredPlayerStats(playerStats, weaponStats, weaponTypes, selectedWeaponType, selectedWeapon) {
  if (!playerStats) return null;
  if (!selectedWeaponType && !selectedWeapon) return playerStats;
  if (!weaponStats) return playerStats;

  let relevantWeaponStats = [];
  if (selectedWeapon) {
    const ws = weaponStats.find((w) => w.weapon_name === selectedWeapon);
    if (ws) relevantWeaponStats = [ws];
  } else if (selectedWeaponType) {
    relevantWeaponStats = weaponTypes[selectedWeaponType] || [];
  }
  if (relevantWeaponStats.length === 0) return playerStats;

  // Stats that need instance-weighted averaging (use _instances count fields)
  const weightedStatsDefs = [
    { stat: 'time_to_kill_average', count: 'time_to_kill_instances' },
    { stat: 'time_to_damage_average', count: 'time_to_damage_instances' },
    { stat: 'crosshair_placement_average', count: 'crosshair_placement_instances' },
    { stat: 'initial_crosshair_placement_average', count: 'initial_crosshair_placement_instances' },
  ];

  // Rate stats derived from two summed fields
  const rateStatsDefs = [
    { stat: 'spotted_hit_rate', numerator: 'spotted_hits', denominator: 'spotted_shots' },
    { stat: 'spray_shot_accuracy', numerator: 'spray_shots_hit', denominator: 'spray_shots_total' },
    { stat: 'headshot_kill_percentage', numerator: 'headshot_kills', denominator: 'kills' },
    { stat: 'headshot_percentage', numerator: 'hitgroup_head_hits', denominator: 'shots_connected' },
    { stat: 'kpr', numerator: 'kills', denominator: 'rounds_with_weapon' },
    { stat: 'adr', numerator: 'damage', denominator: 'rounds_with_weapon' },
    { stat: 'accurate_shots_while_counterstrafing_rate', numerator: 'accurate_counterstrafing', denominator: 'total_counterstrafing' },
  ];

  // Stats that are simply summed across weapons
  const summedStats = [
    'kills', 'assists', 'damage', 'headshot_kills',
    'shots_total', 'shots_connected',
    'spotted_shots', 'spotted_hits',
    'spray_shots_total', 'spray_shots_hit',
    'trade_kill_successes', 'trade_kill_fails',
    'opening_successes', 'opening_fails',
    'rounds_with_weapon',
    'hitgroup_head_hits',
    'accurate_counterstrafing', 'inaccurate_counterstrafing',
  ];

  const result = {};

  // Stats where lower is better — deviation is negated
  const descendingStats = new Set([
    'time_to_kill_average', 'time_to_damage_average',
    'crosshair_placement_average', 'initial_crosshair_placement_average',
    'trade_kill_fails', 'traded_death_fails',
    'util_he_friend_damage', 'util_flash_friends_flashed',
    'util_flash_total_friend_duration', 'util_lost_cost', 'deaths',
  ]);

  // Merge weapon peer data into a stat entry. For a single weapon, use
  // its peer avg directly. For a weapon type (multiple weapons), compute
  // a usage-weighted average of peer data across all relevant weapons.
  function weaponPeer(stat) {
    const weaponsWithPeer = relevantWeaponStats.filter(
      (w) => w[stat]?.rating_range_average_value != null,
    );
    if (weaponsWithPeer.length === 0) {
      return {
        rating_range_average_value: null,
        standard_deviation: null,
        deviations_away: null,
      };
    }

    if (weaponsWithPeer.length === 1) {
      return {
        rating_range_average_value: weaponsWithPeer[0][stat].rating_range_average_value,
        standard_deviation: weaponsWithPeer[0][stat].standard_deviation,
      };
    }

    // Weight by shots_total so heavily-used weapons dominate the average
    let totalWeight = 0; let weightedAvg = 0; let
      weightedStd = 0;
    weaponsWithPeer.forEach((w) => {
      const weight = w.shots_total?.players_value || 1;
      weightedAvg += (w[stat].rating_range_average_value || 0) * weight;
      weightedStd += (w[stat].standard_deviation || 0) * weight;
      totalWeight += weight;
    });

    return {
      rating_range_average_value: totalWeight > 0 ? weightedAvg / totalWeight : null,
      standard_deviation: totalWeight > 0 ? weightedStd / totalWeight : null,
    };
  }

  // Compute deviations_away from weapon peer data after the stat is fully assembled
  function computeDeviation(statEntry, statName) {
    const val = statEntry.players_value;
    const avg = statEntry.rating_range_average_value;
    const std = statEntry.standard_deviation;
    if (val == null || avg == null || std == null || std === 0) return null;
    const raw = (val - avg) / std;
    return descendingStats.has(statName) ? -raw : raw;
  }

  // Weighted averages
  weightedStatsDefs.forEach(({ stat, count }) => {
    let totalValue = 0; let
      totalCount = 0;
    relevantWeaponStats.forEach((weapon) => {
      const v = weapon[stat]?.players_value || 0;
      const c = weapon[count]?.players_value || 0;
      totalValue += v * c;
      totalCount += c;
    });
    if (playerStats[stat]) {
      result[stat] = {
        ...playerStats[stat],
        players_value: totalCount > 0 ? totalValue / totalCount : 0,
        ...weaponPeer(stat),
        // weapon_scoped marks entries the merge really computed from
        // weapon data; breakdowns use it to reject profile fallbacks
        weapon_scoped: true,
      };
    }
  });

  // Summed stats
  summedStats.forEach((stat) => {
    let total = 0;
    relevantWeaponStats.forEach((weapon) => {
      total += weapon[stat]?.players_value || 0;
    });
    const base = playerStats[stat] || {};
    result[stat] = {
      ...base, players_value: total, ...weaponPeer(stat), weapon_scoped: true,
    };
  });

  // Derived sums needed by rate stats
  const accurateCS = result.accurate_counterstrafing?.players_value ?? 0;
  const inaccurateCS = result.inaccurate_counterstrafing?.players_value ?? 0;
  result.total_counterstrafing = { players_value: accurateCS + inaccurateCS, weapon_scoped: true };

  // Rate stats (computed from summed numerator/denominator)
  rateStatsDefs.forEach(({ stat, numerator, denominator }) => {
    if (!denominator) return;
    const num = result[numerator]?.players_value ?? 0;
    const den = result[denominator]?.players_value ?? 0;
    if (playerStats[stat]) {
      result[stat] = {
        ...playerStats[stat],
        players_value: den > 0 ? num / den : 0,
        ...weaponPeer(stat),
        weapon_scoped: true,
      };
    }
  });

  // KDR special case — weapons don't track deaths
  if (result.kills && playerStats.kdr) {
    result.kdr = { ...playerStats.kdr };
  }

  // Copy remaining stats from the overall profile
  Object.keys(playerStats).forEach((key) => {
    if (!result[key]) result[key] = playerStats[key];
  });

  // Recompute deviations for weapon-filtered stats
  Object.keys(result).forEach((key) => {
    const entry = result[key];
    if (entry && typeof entry === 'object' && 'players_value' in entry) {
      entry.deviations_away = computeDeviation(entry, key);
    }
  });

  return result;
}

function getAvailableWeaponTypes(weaponTypes) {
  if (!weaponTypes) return {};

  const availableTypes = {};

  Object.keys(weaponTypes).forEach((type) => {
    if (type !== 'Misc' && weaponTypes[type] && weaponTypes[type].length > 0) {
      availableTypes[type] = weaponTypes[type];
    }
  });

  return availableTypes;
}

// Long names live under shared.stat_names.<stat_key>; unknown keys fall through as-is.
function statFriendlyName(statKey) {
  const key = `shared.stat_names.${statKey}`;
  return i18n.global.te(key) ? t(key) : statKey;
}

export {
  getEquipmentTypeByName,
  categorizeWeapons,
  getAvailableWeapons,
  getFilteredPlayerStats,
  getAvailableWeaponTypes,
  statFriendlyName,
};
